<template>
  <div class="drawer-container">
    <div class="drawer-header">
      <div class="flex align-center justify-between mb-3">
        <div class="text-24 text-bold">
          AI 貿易書類照合結果（複数書類）
        </div>
        <div class="flex gap-2">
          <el-button @click="handleClose" type="default" icon="el-icon-close">
            前の画面に戻る
          </el-button>
        </div>
      </div>
      <el-alert
        type="success"
        effect="dark"
        :closable="false"
        class="custom-success-alert"
      >
        <div>
          選択された{{ selectedDocuments.length }}件の書類を比較しています。
        </div>
      </el-alert>
    </div>

    <div class="drawer-body flex pt-2 pb-4">
      <div class="content-area">
        <!-- 選択されたドキュメント情報 -->
        <div class="selected-documents-info mb-4">
          <h3 class="text-18 text-bold mb-3">選択された書類</h3>

          <div class="documents-layout">
            <!-- 左側: 基準とする書類 -->
            <div class="base-document-section">
              <h4 class="section-subtitle">基準とする書類</h4>
              <el-collapse v-model="activeBaseDocument" class="document-collapse">
                <el-collapse-item
                  :name="'0'"
                  class="base-document-item"
                >
                  <template slot="title">
                    <div class="collapse-header">
                      <span class="document-title pl-2">
                        <span class="text-bold">{{ docLabels[0] }}</span>
                        <span class="document-name ml-2">{{ selectedDocuments[0].documentName }}</span>
                      </span>
                      <span class="process-name ml-2">{{ selectedDocuments[0].processName }}</span>
                    </div>
                  </template>
                  <div class="collapse-body">
                    <div class="info-row">
                      <span class="label">ファイル名:</span>
                      <span class="value">{{ selectedDocuments[0].documentName }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">プロセス名:</span>
                      <span class="value">{{ selectedDocuments[0].processName }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">日付:</span>
                      <span class="value">{{ selectedDocuments[0].date }}</span>
                    </div>

                    <!-- PDFプレビューエリア -->
                    <div class="pdf-preview-container">
                      <div v-if="pdfUrls[selectedDocuments[0].documentManageId]" class="pdf-preview-wrapper">
                        <div class="pdf-preview-header">
                          <el-button
                            type="text"
                            size="small"
                            @click="closePdfPreview(selectedDocuments[0].documentManageId)"
                            icon="el-icon-close"
                          >
                            プレビューを閉じる
                          </el-button>
                        </div>
                        <iframe
                          :src="pdfUrls[selectedDocuments[0].documentManageId]"
                          class="pdf-preview-iframe"
                        ></iframe>
                      </div>
                      <div v-else-if="pdfLoading[selectedDocuments[0].documentManageId]" class="pdf-loading">
                        <i class="el-icon-loading"></i> PDFを読み込み中...
                      </div>
                      <div v-else class="pdf-placeholder">
                        <el-button
                          type="primary"
                          size="small"
                          @click="loadPdfPreview(selectedDocuments[0])"
                          icon="el-icon-document"
                        >
                          PDFをプレビュー
                        </el-button>
                        <el-button
                          type="default"
                          size="small"
                          @click="handleOpenPdfInNewWindow(selectedDocuments[0])"
                          icon="el-icon-top-right"
                          :loading="pdfLoading[selectedDocuments[0].documentManageId]"
                          class="ml-2"
                        >
                          別タブでプレビュー
                        </el-button>
                      </div>
                    </div>
                  </div>
                </el-collapse-item>
              </el-collapse>
            </div>

            <!-- 右側: 比較する書類 -->
            <div class="compare-documents-section">
              <h4 class="section-subtitle">比較する書類</h4>
              <el-collapse v-model="activeCompareDocuments" class="document-collapse">
                <el-collapse-item
                  v-for="(doc, index) in selectedDocuments.slice(1)"
                  :key="index + 1"
                  :name="String(index + 1)"
                >
                  <template slot="title">
                    <div class="collapse-header">
                      <span class="document-title pl-2">
                        <span class="text-bold">{{ docLabels[index + 1] }}</span>
                        <span class="document-name ml-2">{{ doc.documentName }}</span>
                      </span>
                      <span class="process-name ml-2">{{ doc.processName }}</span>
                    </div>
                  </template>
                  <div class="collapse-body">
                    <div class="info-row">
                      <span class="label">ファイル名:</span>
                      <span class="value">{{ doc.documentName }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">プロセス名:</span>
                      <span class="value">{{ doc.processName }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">日付:</span>
                      <span class="value">{{ doc.date }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">サイズ:</span>
                      <span class="value">{{ doc.size }}</span>
                    </div>

                    <!-- PDFプレビューエリア -->
                    <div class="pdf-preview-container">
                      <div v-if="pdfUrls[doc.documentManageId]" class="pdf-preview-wrapper">
                        <div class="pdf-preview-header">
                          <el-button
                            type="text"
                            size="small"
                            @click="closePdfPreview(doc.documentManageId)"
                            icon="el-icon-close"
                          >
                            プレビューを閉じる
                          </el-button>
                        </div>
                        <iframe
                          :src="pdfUrls[doc.documentManageId]"
                          class="pdf-preview-iframe"
                        ></iframe>
                      </div>
                      <div v-else-if="pdfLoading[doc.documentManageId]" class="pdf-loading">
                        <i class="el-icon-loading"></i> PDFを読み込み中...
                      </div>
                      <div v-else class="pdf-placeholder">
                        <el-button
                          type="primary"
                          size="small"
                          @click="loadPdfPreview(doc)"
                          icon="el-icon-document"
                        >
                          PDFをプレビュー
                        </el-button>
                        <el-button
                          type="default"
                          size="small"
                          @click="handleOpenPdfInNewWindow(doc)"
                          icon="el-icon-top-right"
                          :loading="pdfLoading[doc.documentManageId]"
                          class="ml-2"
                        >
                          別タブでプレビュー
                        </el-button>
                      </div>
                    </div>
                  </div>
                </el-collapse-item>
              </el-collapse>
            </div>
          </div>
        </div>

        <!-- 比較結果タブ -->
        <el-tabs v-model="activeTab" class="comparison-tabs">
          <!-- ヘッダー項目タブ -->
          <el-tab-pane label="ヘッダー項目" name="header">
            <div class="tab-content">
              <div class="action-buttons mb-3">
                <el-button
                  @click="downloadHeaderTSV"
                  type="normal"
                  size="small"
                  icon="el-icon-download"
                >
                  TSVダウンロード
                </el-button>
              </div>

              <div class="table-container">
                <el-table
                  :data="headerComparisonData"
                  border
                  stripe
                  style="width: 100%"
                  class="comparison-table"
                >
                  <el-table-column prop="fieldName" label="項目名" width="200" fixed />
                  <el-table-column
                    v-for="(doc, index) in selectedDocuments"
                    :key="index"
                    :label="docLabels[index]"
                    min-width="150"
                    :header-class-name="index === 0 ? 'base-document-header' : ''"
                  >
                    <template slot-scope="{ row }">
                      <span :class="{ 'text-bold text-error': row.judgment === 'NG' }">
                        {{ row.values[index] }}
                      </span>
                    </template>
                  </el-table-column>
                  <el-table-column label="判定" width="80" align="center">
                    <template slot-scope="{ row }">
                      <el-tag
                        :type="row.judgment === 'OK' ? 'success' : 'danger'"
                        size="small"
                      >
                        {{ row.judgment }}
                      </el-tag>
                    </template>
                  </el-table-column>
                  <el-table-column label="差分" width="300">
                    <template slot-scope="{ row }">
                      <div style="white-space: pre-line;">
                        {{ row.differences }}
                      </div>
                    </template>
                  </el-table-column>
                </el-table>

                <!-- 合計金額の確認セクション -->
                <cross-check-amount-verification
                  :selected-documents="selectedDocuments"
                  :compare-data="compareData"
                />
              </div>
            </div>
          </el-tab-pane>

          <!-- 明細項目タブ -->
          <el-tab-pane label="明細項目" name="detail">
            <div class="tab-content">
              <CrossCheckItemsMultiComparison
                :compareData="compareData"
                :selectedDocuments="selectedDocuments"
                @download-tsv="downloadDetailTSV"
              />
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>
    </div>
  </div>
</template>

<script>
import CrossCheckItemsMultiComparison from './crossCheckItemsMultiComparison.vue';
import CrossCheckAmountVerification from './crossCheckAmountVerification.vue';
import { usePdfPreview } from '@/composables/usePdfPreview';

export default {
  name: 'CrossCheckAiOcrMultiDetail',
  components: {
    CrossCheckItemsMultiComparison,
    CrossCheckAmountVerification,
  },
  setup() {
    const { openPdfInNewWindow, downloadPdf } = usePdfPreview();
    return { openPdfInNewWindow, downloadPdf };
  },
  props: {
    selectedDocuments: {
      type: Array,
      default: () => [],
    },
    compareData: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeTab: 'header',
      headerComparisonData: [],
      activeBaseDocument: [],
      activeCompareDocuments: [],
      pdfLoading: {},
      pdfUrls: {},
      displayHeaderFields: [
        'poNo',
        'contractNo',
        'buyerName',
        'buyerAddress1',
        'buyerAddress2',
        'buyerAddress3',
        'buyerAddress4',
        'consigneeName',
        'consigneeAddress1',
        'consigneeAddress2',
        'consigneeAddress3',
        'consigneeAddress4',
        'shipperName',
        'shipperAddress1',
        'shipperAddress2',
        'shipperAddress3',
        'shipperAddress4',
        'portOfLoadingName',
        'portOfDischargingName',
        'paymentTerms1',
        'deliveryTermsId',
        'deliveryTermsPlaceName',
        'exCustomsTotalQuantity',
        'detailRowCount',
        'exCustomsTotalAmount',
      ],
      displayFieldLabels: {
        poNo: 'P/O No.',
        contractNo: 'Contract No.',
        buyerName: 'Buyer Name',
        buyerAddress1: 'Buyer Address1',
        buyerAddress2: 'Buyer Address2',
        buyerAddress3: 'Buyer Address3',
        buyerAddress4: 'Buyer Address4',
        consigneeName: 'Consignee Name',
        consigneeAddress1: 'Consignee Address1',
        consigneeAddress2: 'Consignee Address2',
        consigneeAddress3: 'Consignee Address3',
        consigneeAddress4: 'Consignee Address4',
        shipperName: 'Shipper Name',
        shipperAddress1: 'Shipper Address1',
        shipperAddress2: 'Shipper Address2',
        shipperAddress3: 'Shipper Address3',
        shipperAddress4: 'Shipper Address4',
        portOfLoadingName: '（ETD）Port of Loading',
        portOfDischargingName: 'Port of Discharging',
        paymentTerms1: 'Payment Terms',
        deliveryTermsId: 'Delivery Terms (Incoterms)',
        deliveryTermsPlaceName: 'Delivery Terms (Place)',
        exCustomsTotalQuantity: '合計数量',
        detailRowCount: '明細行数',
        exCustomsTotalAmount: '合計金額',
      },
    };
  },
  computed: {
    docLabels() {
      const counts = {};
      this.selectedDocuments.forEach((doc) => {
        const cat = doc.documentCategory || '';
        counts[cat] = (counts[cat] || 0) + 1;
      });
      const indices = {};
      return this.selectedDocuments.map((doc, i) => {
        const cat = doc.documentCategory || `書類${i + 1}`;
        if (counts[cat] > 1) {
          indices[cat] = (indices[cat] || 0) + 1;
          return `${cat}-${indices[cat]}`;
        }
        return cat;
      });
    },
  },
  watch: {
    compareData: {
      handler(newVal) {
        if (newVal && newVal.documents) {
          this.compareHeaderItems();
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    handleClose() {
      this.$emit('close');
    },
    async loadPdfPreview(doc) {
      if (this.pdfUrls[doc.documentManageId]) {
        return;
      }
      this.$set(this.pdfLoading, doc.documentManageId, true);
      try {
        const src = await this.downloadPdf(
          doc.documentManageId,
          doc.documentTrx,
          doc.documentName,
        );
        this.$set(this.pdfUrls, doc.documentManageId, src);
      } catch (err) {
        console.error('PDFプレビュー読み込みエラー:', err);
        this.$message.error('PDFの取得に失敗しました');
      } finally {
        this.$set(this.pdfLoading, doc.documentManageId, false);
      }
    },
    async handleOpenPdfInNewWindow(doc) {
      try {
        await this.openPdfInNewWindow(
          doc.documentManageId,
          doc.documentTrx,
          doc.documentName,
        );
      } catch (err) {
        this.$message.error('PDFの取得に失敗しました');
      }
    },
    closePdfPreview(documentManageId) {
      this.$delete(this.pdfUrls, documentManageId);
    },
    compareHeaderItems() {
      const documents = this.compareData?.documents || [];
      if (documents.length < 2) return;

      const comparisonResults = [];

      this.displayHeaderFields.forEach((fieldName) => {
        // 各書類からフィールド値を収集
        const values = documents.map((doc) => {
          const items = doc.ocrResults?.aiocrItems || [];
          // exCustoms フィールドが見つからない場合 imCustoms をフォールバック
          const item =
            items.find((i) => i.twFieldName === fieldName) ||
            (fieldName === 'exCustomsTotalAmount'
              ? items.find((i) => i.twFieldName === 'imCustomsTotalAmount')
              : null);

          // 特殊処理: 明細行数
          if (fieldName === 'detailRowCount') {
            const goodsLine =
              doc.ocrResults?.aiocrTableItems?.goodsLine || [];
            return String(goodsLine.length);
          }

          // 特殊処理: 合計金額（通貨単位を隣に表示）
          if (fieldName === 'exCustomsTotalAmount') {
            const currencyItem =
              items.find((i) => i.twFieldName === 'exCustomsCurrency') ||
              items.find((i) => i.twFieldName === 'imCustomsCurrency');
            const currency = currencyItem?.ocrFieldValue || '';
            const amount = item?.ocrFieldValue || '';
            return amount
              ? `${amount}${currency ? ' ' + currency : ''}`
              : '';
          }

          return item?.ocrFieldValue || '';
        });

        // 特殊処理: 合計金額の通貨不一致判定
        // 通貨が異なる場合は比較をスキップし固定メッセージを出力
        if (fieldName === 'exCustomsTotalAmount') {
          const currencies = documents.map((doc) => {
            const items = doc.ocrResults?.aiocrItems || [];
            const currencyItem =
              items.find(
                (i) => i.twFieldName === 'exCustomsCurrency',
              ) ||
              items.find(
                (i) => i.twFieldName === 'imCustomsCurrency',
              );
            return currencyItem?.ocrFieldValue || '';
          });
          const currencyMismatch =
            new Set(currencies).size > 1;
          if (currencyMismatch) {
            comparisonResults.push({
              id: comparisonResults.length + 1,
              fieldName:
                this.displayFieldLabels[fieldName] || fieldName,
              values: values.map((v) => v || '( no result )'),
              judgment: 'NG',
              differences:
                '単位通貨が異なるため、チェックしません',
            });
            return;
          }
        }

        // 全て一致しているか判定
        const uniqueValues = [...new Set(values.map((v) => v))];
        // 全て同じ値であればOK（空文字列でも一致していればOK）
        const allMatch = uniqueValues.length === 1;
        const judgment = allMatch ? 'OK' : 'NG';

        // 差分情報の作成
        let differences = '一致しています';
        if (!allMatch) {
          // 特殊処理: 合計金額の差分計算
          if (fieldName === 'exCustomsTotalAmount') {
            const baseNum = parseFloat(
              String(values[0]).replace(/,/g, ''),
            );
            const canCalc = !Number.isNaN(baseNum) && baseNum !== 0;

            const diffList = values
              .map((v, i) => {
                const displayValue = v || '( no result )';
                if (i === 0) {
                  return `${this.docLabels[0]}: ${displayValue}`;
                }
                if (canCalc) {
                  const num = parseFloat(String(v).replace(/,/g, ''));
                  if (!Number.isNaN(num)) {
                    const diffAmount = num - baseNum;
                    const diffPercent = (
                      (diffAmount / baseNum) *
                      100
                    ).toFixed(1);
                    return `${this.docLabels[i]}: ${displayValue}\n・差分：${diffAmount.toLocaleString()}（${diffPercent}%）`;
                  }
                }
                return `${this.docLabels[i]}: ${displayValue}`;
              })
              .join('\n');

            differences = `差異があります。\n${diffList}`;
          } else {
            const diffList = values
              .map((v, i) => `${this.docLabels[i]}: ${v || '( no result )'}`)
              .join('\n');
            differences = `差異があります。\n${diffList}`;
          }
        }

        comparisonResults.push({
          id: comparisonResults.length + 1,
          fieldName: this.displayFieldLabels[fieldName] || fieldName,
          values: values.map((v) => v || '( no result )'),
          judgment,
          differences,
        });
      });

      this.headerComparisonData = comparisonResults;
    },
    downloadHeaderTSV() {
      if (this.headerComparisonData.length === 0) {
        this.$message.warning('ダウンロードするデータがありません');
        return;
      }

      try {
        // ヘッダー行
        const headers = [
          '項目名',
          ...this.docLabels,
          '判定',
          '差分',
        ];

        // データ行
        // 差分セル内の改行はセル分割の原因になるため半角スペースに置換
        // （TSV/Excel で1セル＝1項目として扱うため）
        const rows = this.headerComparisonData.map((row) => [
          row.fieldName || '',
          ...row.values.map((v) => v || '( no result )'),
          row.judgment || '',
          row.differences ? row.differences.replace(/\n/g, ' ') : '',
        ]);

        // TSV形式に変換
        const tsvContent = [headers, ...rows]
          .map((row) => row.join('\t'))
          .join('\n');

        // BOM付きUTF-8でダウンロード
        const bom = '\uFEFF';
        const blob = new Blob([bom + tsvContent], {
          type: 'text/tab-separated-values;charset=utf-8;',
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const timestamp = new Date()
          .toISOString()
          .replace(/[-:]/g, '')
          .replace('T', '_')
          .split('.')[0];
        link.download = `header_comparison_multi_${timestamp}.tsv`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        this.$message.success('TSVファイルをダウンロードしました');
      } catch (error) {
        console.error('TSVダウンロードエラー:', error);
        this.$message.error('TSVダウンロードに失敗しました');
      }
    },
    downloadDetailTSV(detailData) {
      if (!detailData || detailData.length === 0) {
        this.$message.warning('ダウンロードするデータがありません');
        return;
      }

      try {
        // ヘッダー行
        const headers = [
          '明細No',
          `${this.docLabels[0]}_商品名`,
          `${this.docLabels[0]}_商品番号`,
          `${this.docLabels[0]}_数量`,
          ...this.selectedDocuments
            .slice(1)
            .map((_, i) => `${this.docLabels[i + 1]}_マッチ件数`),
          '判定',
        ];

        // データ行
        const rows = detailData.map((row) => {
          const quantity = row.baseItem.ocrQuantity || '';
          const unit = row.baseItem.ocrUnit || '';
          const quantityStr =
            quantity && unit
              ? `${quantity} ${unit}`
              : quantity || unit || '( no result )';

          return [
            row.lineNo || '',
            row.baseItem.ocrGoodsShortName || '( no result )',
            row.baseItem.ocrGoodsRefNo1 || '( no result )',
            quantityStr,
            ...row.matchingItems.map((m) => `${m.items.length}件`),
            row.judgment || '',
          ];
        });

        // TSV形式に変換
        const tsvContent = [headers, ...rows]
          .map((row) => row.join('\t'))
          .join('\n');

        // BOM付きUTF-8でダウンロード
        const bom = '\uFEFF';
        const blob = new Blob([bom + tsvContent], {
          type: 'text/tab-separated-values;charset=utf-8;',
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const timestamp = new Date()
          .toISOString()
          .replace(/[-:]/g, '')
          .replace('T', '_')
          .split('.')[0];
        link.download = `detail_comparison_multi_${timestamp}.tsv`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        this.$message.success('TSVファイルをダウンロードしました');
      } catch (error) {
        console.error('TSVダウンロードエラー:', error);
        this.$message.error('TSVダウンロードに失敗しました');
      }
    },
  },
};
</script>

<style scoped lang="scss">
.drawer-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f5f7fa;
}

.drawer-header {
  padding: 20px 24px;
  background-color: #fff;
  border-bottom: 1px solid #e4e7ed;
}

.drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 0 24px;
}

.content-area {
  width: 100%;
  max-width: 1800px;
  margin: 0 auto;
}

.selected-documents-info {
  background-color: #fff;
  border-radius: 4px;
  padding: 20px;
}

.documents-layout {
  display: flex;
  gap: 20px;
}

.base-document-section {
  flex: 0 0 35%;
  min-width: 300px;
}

.compare-documents-section {
  flex: 1;
  min-width: 300px;
}

.section-subtitle {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: bold;
  color: #606266;
}

.document-collapse {
  border: 1px solid #e4e7ed;
  border-radius: 4px;

  ::v-deep .el-collapse-item__arrow {
    transform: rotate(90deg);
  }

  ::v-deep .el-collapse-item__arrow.is-active {
    transform: rotate(90deg);
  }
}

.collapse-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-right: 10px;
}

.document-title {
  flex: 1;
}

.document-name {
  color: #606266;
}

.process-name {
  color: #909399;
  font-size: 12px;
  margin-left: auto;
  padding-right: 10px;
}

.collapse-body {
  padding: 16px 20px;
  background-color: #f5f7fa;
  border-top: 1px solid #ebeef5;
}

.pdf-preview-container {
  margin-top: 20px;
  border-top: 1px solid #ebeef5;
  padding-top: 20px;
}

.pdf-preview-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.pdf-preview-header {
  width: 100%;
  max-width: 800px;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}

.pdf-preview-iframe {
  width: 100%;
  max-width: 800px;
  height: 350px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
}

.pdf-loading {
  text-align: center;
  padding: 40px;
  color: #909399;
  font-size: 14px;
}

.pdf-placeholder {
  text-align: center;
  padding: 40px;
}

.info-row {
  display: flex;
  margin-bottom: 8px;
  font-size: 14px;
}

.info-row .label {
  font-weight: 600;
  color: #606266;
  min-width: 100px;
}

.info-row .value {
  flex: 1;
  color: #303133;
  word-break: break-all;
}

.comparison-tabs {
  background-color: #fff;
  border-radius: 4px;
  padding: 20px;
}

// ヘッダー項目テーブルの書類1列をハイライト（明細項目と同じスタイル）
::v-deep .el-table__header .base-document-header {
  background-color: #ecf5ff !important;
  color: #222 !important;
}

::v-deep .el-table__body .base-document-column {
  background-color: #f9feff !important;
}

.tab-content {
  padding: 10px 0;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.table-container {
  overflow-x: auto;
}

.comparison-table {
  min-width: 100%;
  font-size: 12px;
  color: #303133;
}

.text-error {
  color: #f56c6c;
}

.text-bold {
  font-weight: bold;
}
</style>
